import React, { useEffect, useState } from "react";
import { ErrorMessage, FormikProps } from "formik";
import { FormInitialValues } from "./DownloadDialogWrapper";
import ATMLoadingButton from "src/components/UI/atoms/ATMLoadingButton/ATMLoadingButton";
import { MdEmail } from "react-icons/md";
import ATMHTMLEditor from "src/components/UI/atoms/formFields/ATMHTMLEditor/ATMHTMLEditor";
import { AffidavitCertificateUpdateListResponse } from "src/models/AffidavitCertificateUpdate.model";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { useAddAffidavitCertificateMutation } from "src/services/AffidavitCertificateService";
import {
  useLazyGetPanFormByPanIdQuery,
  useSavePanFormImagesMutation,
} from "src/services/PANService";
import { panFormToCertificate } from "src/pages/pan/view/Dialog/PanFormDialogBox/panFormToCertificate";
import { showToast } from "src/utils/toaster/showToast";
import { ackFileBaseName, fileExtensionFromUrl } from "src/utils/downloadFileAs";
import { stripStateAndPincode } from "src/utils/stripStateAndPincode";
import { cleanSignatureImage } from "src/pages/pan/view/Dialog/PanFormDialogBox/signatureInk";
import AffidavitTemplate, { affidavitHtml } from "src/pages/affidavit-certificate/AffidavitTemplate";
import {
  AffidavitMarks,
  AffidavitSerial,
  AffidavitStamps,
  affidavitMarks,
  isNotaryOn,
  loadAffidavitStamps,
  urlToDataUrl,
} from "src/pages/affidavit-certificate/affidavitStamps";
import {
  useLazyGetAffidavitForPanQuery,
  useLazyGetAffidavitStampQuery,
  useReserveAffidavitSerialMutation,
} from "src/services/AffidavitStampService";

type Props = {
  businessEnquiryData: AffidavitCertificateUpdateListResponse | null;
  onClose: () => void;
  /**
   * "Update Affidavit": reopen an affidavit already generated with the
   * details it was printed with, and generate it again (same serial number).
   */
  isUpdate?: boolean;
  /** Called after a new PDF is saved, e.g. to refresh the caller's row. */
  onGenerated?: () => void;
};

const DownloadDialog = ({ businessEnquiryData, onClose, isUpdate, onGenerated }: Props) => {
  const [randomNumber, setRandomNumber] = useState<number | null>(null);
  const [todayDate, setTodayDate] = useState<string>("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // Editable fields - initialized from data, user can correct before generating
  const [applicantName, setApplicantName] = useState<string>("");
  const [fatherName, setFatherName] = useState<string>("");
  const [address, setAddress] = useState<string>("");
  const [dob, setDob] = useState<string>("");
  const [place, setPlace] = useState<string>("");
  /**
   * The applicant's signature, printed above "SIGNATURE / THUMB IMPRESSION" —
   * the same one the PAN form prints in its declaration box.
   */
  const [signature, setSignature] = useState<string>("");
  const [printSignature, setPrintSignature] = useState(true);
  /** Tickets and seals from Affidavit Stamp & Seal, as data-URLs. */
  const [stamps, setStamps] = useState<AffidavitStamps | null>(null);
  /**
   * The serial number the preview shows: this application's own, or the next
   * free one (only taken when Download is pressed).
   */
  const [previewSerial, setPreviewSerial] = useState<AffidavitSerial | null>(null);
  const [hasOwnSerial, setHasOwnSerial] = useState(false);
  /** Notary OFF (Affidavit Stamp & Seal): plain affidavit, no serial taken. */
  const [notaryOn, setNotaryOn] = useState(false);
  const [previewMarks, setPreviewMarks] = useState<AffidavitMarks | null>(null);

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const generateRandomNumber = () => {
    const random = Math.floor(Math.random() * 5) + 1;
    setRandomNumber(random);
  };

  const formatDate = () => {
    const today = new Date();
    const day = today.getDate();
    const year = today.getFullYear();
    const month = monthNames[today.getMonth()];
    setTodayDate(`${day} ${month} ${year}`);
  };

  const formatDOB = (dobStr: string | undefined) => {
    if (!dobStr) return "DD/MM/YYYY";
    const date = new Date(dobStr);
    if (isNaN(date.getTime())) return dobStr;
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatFormDate = (dateStr: string | undefined) => {
    if (!dateStr) return todayDate;
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return todayDate;
    const day = date.getDate();
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  // Use name as fallback if acknowledgement_name looks like an ack number
  const getApplicantName = () => {
    const ackName = businessEnquiryData?.acknowledgement_name;
    if (ackName && !/^\d+$/.test(ackName.trim())) {
      return ackName;
    }
    return businessEnquiryData?.name || ackName || "";
  };

  // Track whether fields have been initialized to prevent re-init on re-renders
  const [isInitialized, setIsInitialized] = useState(false);
  // Where the applicant details on screen came from, shown above the preview so
  // the operator knows whether anything still needs typing.
  const [prefillSource, setPrefillSource] = useState<
    "LOADING" | "PAN_FORM" | "APPLICATION"
  >("LOADING");

  const [fetchPanForm] = useLazyGetPanFormByPanIdQuery();
  const [fetchStampSettings] = useLazyGetAffidavitStampQuery();
  const [fetchAffidavitForPan] = useLazyGetAffidavitForPanQuery();
  const [reserveSerial] = useReserveAffidavitSerialMutation();

  useEffect(() => {
    let cancelled = false;
    fetchStampSettings(undefined)
      .then((res: any) => {
        if (!cancelled) setNotaryOn(isNotaryOn(res?.data?.data));
        return loadAffidavitStamps(res?.data?.data);
      })
      .then((loaded) => {
        if (!cancelled) setStamps(loaded);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    affidavitMarks(previewSerial).then((marks) => {
      if (!cancelled) setPreviewMarks(marks);
    });
    return () => {
      cancelled = true;
    };
  }, [previewSerial]);

  useEffect(() => {
    generateRandomNumber();
    formatDate();
  }, []);

  useEffect(() => {
    if (!businessEnquiryData || isInitialized) return;

    /**
     * Pre-fill the editable fields once, preferring the PAN form the operator
     * filled for this application: it is the reviewed source of truth.
     * `acknowledgement_*` on the PAN application (typed at login) and the raw
     * application fields remain the fallbacks, so anything generated before
     * the PAN form existed still fills in.
     */
    const prefill = async () => {
      let fromForm: ReturnType<typeof panFormToCertificate> = {};
      const panId = businessEnquiryData?._id;
      // Serial number + the details it was last generated with.
      let saved: Record<string, string> | null = null;
      if (panId) {
        try {
          const res: any = await fetchAffidavitForPan(panId);
          const data = res?.data?.data;
          setHasOwnSerial(!!data?.serial);
          setPreviewSerial(data?.serial || data?.nextSerial || null);
          saved = data?.details || null;
        } catch {
          // No serial preview; one is still given on Download.
        }
      }
      if (panId) {
        try {
          const res: any = await fetchPanForm(panId);
          fromForm = panFormToCertificate(
            res?.data?.data?.formData,
            res?.data?.data?.applicationType
          );
        } catch {
          // A failed lookup is not fatal — fall through to the old sources.
          fromForm = {};
        }
      }

      /*
        Update Affidavit: the details exactly as last printed, corrections and
        all. An affidavit from before details were saved falls through to the
        PAN form like a first generate.
      */
      if (isUpdate && saved && Object.keys(saved).length) {
        fromForm = {
          ...fromForm,
          ...(saved.applicantName ? { applicantName: saved.applicantName } : {}),
          ...(saved.fatherName ? { fatherName: saved.fatherName } : {}),
          ...(saved.address ? { affidavitAddress: saved.address } : {}),
          ...(saved.dob ? { dob: saved.dob } : {}),
          ...(saved.place ? { place: saved.place } : {}),
        };
      }

      setApplicantName(fromForm.applicantName || getApplicantName());
      setFatherName(
        fromForm.fatherName ||
          businessEnquiryData?.acknowledgement_father_name ||
          businessEnquiryData?.parentName ||
          ""
      );
      /*
        Residence address: the PAN form first, then the PAN application's
        `acknowledgement_address` (typed at login), then nothing.

        `appliedByUserCurrentAddress` used to sit at the end of this chain and
        is NOT a third source — it is the RETAILER's / distributor's own
        address. An affidavit swears to where the APPLICANT lives, so filling
        it with the counter's address does not leave the box usefully
        pre-filled, it prints a wrong address that reads as a right one. Left
        empty, the operator sees the box is empty and types the real one.
      */
      // The affidavit takes address lines 1 and 2 + district only, same as the
      // XLSX export. Pre-fill only — the operator can still edit the box.
      setAddress(
        fromForm.affidavitAddress ||
          stripStateAndPincode(
            businessEnquiryData?.acknowledgement_address || ""
          )
      );
      // Initialize DOB for editing
      if (fromForm.dob) {
        setDob(fromForm.dob);
      } else if (businessEnquiryData?.dob) {
        setDob(formatDOB(businessEnquiryData.dob));
      }
      // Initialize Place
      setPlace(
        fromForm.place ||
          businessEnquiryData?.acknowledgement_place ||
          businessEnquiryData?.retailerData?.district ||
          businessEnquiryData?.distributorData?.district ||
          ""
      );
      // Date stays the application's own date — the notary register is keyed on
      // when the application was filed, not on when the form was typed.
      if (businessEnquiryData?.appliedOnDate) {
        setTodayDate(formatFormDate(businessEnquiryData.appliedOnDate));
      } else if (businessEnquiryData?.createdAt) {
        setTodayDate(formatFormDate(businessEnquiryData.createdAt));
      }

      /*
        Signature: the copy cropped / cleaned in the PAN form (saved as
        editedSignatureUrl on "Next: Preview") is exactly what the PAN form
        printed, so it is used as-is. Without one, the applicant's own upload
        gets the same ink-only clean-up the PAN form gives it on open, so it
        prints as a signature rather than a grey photo of a slip.
      */
      const editedSig = businessEnquiryData?.editedSignatureUrl || "";
      const originalSig = businessEnquiryData?.signaturePhotoUrl || "";
      if (editedSig) {
        setSignature((await urlToDataUrl(editedSig)) || editedSig);
      } else if (originalSig) {
        setSignature(
          (await cleanSignatureImage(originalSig)) ||
            (await urlToDataUrl(originalSig)) ||
            originalSig
        );
      }

      setPrefillSource(
        Object.keys(fromForm).length ? "PAN_FORM" : "APPLICATION"
      );
      setIsInitialized(true);
    };

    prefill();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessEnquiryData, isInitialized]);

  const [addAffidavitceCertificate] = useAddAffidavitCertificateMutation();
  const [savePanFormImages] = useSavePanFormImagesMutation();

  /*
    The signature this affidavit printed, stored as the application's
    `<ack>_Sig` (editedSignatureUrl) so the PAN form opens on the same one.

    Only when the application has no stored copy yet: one saved from the PAN
    form is already what this dialog printed, and re-saving it would only
    churn the file. The photo is left out of the call, so it is untouched.
    Fire-and-forget: a failure only warns, the affidavit is already done.
  */
  const storeSignatureForPanForm = () => {
    if (
      !businessEnquiryData?._id ||
      businessEnquiryData?.editedSignatureUrl ||
      !printSignature ||
      !signature.startsWith("data:image/")
    ) {
      return;
    }
    savePanFormImages({ panId: businessEnquiryData._id, signature }).then(
      (res: any) => {
        if (res?.error || !res?.data?.status) {
          showToast(
            "error",
            res?.error?.data?.message ||
              res?.data?.message ||
              "Could not save the signature for the PAN form."
          );
        }
      }
    );
  };

  const generatePDF = async () => {
    if (isGenerating) return;
    setIsGenerating(true);
    const panId = businessEnquiryData?._id;
    /*
      The serial number is taken now, not when the dialog opened, so closing
      without downloading never uses one up. An application keeps the number
      it was given, so Update / a retry reprints the same one.
    */
    let serial: AffidavitSerial | null = null;
    if (panId && notaryOn) {
      const res: any = await reserveSerial({ panId });
      if (res?.error || res?.data?.status === false) {
        showToast(
          "error",
          res?.error?.data?.message ||
            res?.data?.message ||
            "Could not get the affidavit serial number. Try again."
        );
        setIsGenerating(false);
        return;
      }
      serial = res?.data?.data || null;
    }
    const details = {
      applicantName,
      fatherName,
      dob,
      address,
      place,
    };
    const html = affidavitHtml({
      ...details,
      todayDate,
      signature: printSignature ? signature : "",
      acknowledgementNumber: businessEnquiryData?.acknowledgementNumber,
      stamps,
      marks: await affidavitMarks(serial),
    });
    addAffidavitceCertificate({
      html,
      panId,
      details,
    }).then((res: any) => {
      if (res.error) {
        showToast("error", res?.error?.data?.message);
        setIsGenerating(false);
      } else {
        if (
          res?.data &&
          res?.data?.data &&
          res?.data?.data?.affidavitertificateFile
        ) {
          storeSignatureForPanForm();
          downloadPdfFile(res?.data?.data?.affidavitertificateFile);
          showToast("success", res?.data?.message);
          onGenerated?.();
          onClose();
        } else {
          setIsGenerating(false);
        }
      }
    }).catch(() => {
      setIsGenerating(false);
    });
  };

  const downloadPdfFile = async (pdfUrl: string) => {
    try {
      const response = await fetch(pdfUrl, {
        method: "GET",
      });

      if (!response.ok) {
        throw new Error("Failed to fetch the PDF file.");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const segments = pdfUrl.split(/[\/?#]/);
      // `<ack>_Affidavit.pdf` when the application has an acknowledgement
      // number, else the server's own file name.
      const ack = businessEnquiryData?.acknowledgementNumber;
      const fileName = ack
        ? `${ackFileBaseName(ack, "Affidavit")}.${fileExtensionFromUrl(pdfUrl) || "pdf"}`
        : segments[segments.length - 1] || "downloaded-file.pdf";

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading PDF:", error);
    }
  };
  
  return (
    <>
      {/* Where the applicant details came from. Sits OUTSIDE #pdfContent, so it
          is never part of the generated PDF. */}
      {prefillSource !== "LOADING" && (
        <div
          style={{
            padding: "8px 20px",
            fontSize: "12.5px",
            borderBottom: "1px solid #ddd",
            background: prefillSource === "PAN_FORM" ? "#f1f8f1" : "#fdf6e3",
            color: "#333",
          }}
        >
          {prefillSource === "PAN_FORM"
            ? "Details filled from the PAN form of this application. Use Edit to correct anything before downloading."
            : "No PAN form found for this application — details below come from the application record. Fill the PAN form first, or use Edit to type them."}
        </div>
      )}

      {/* The residence address is the one detail with no safe fallback: a DSA /
          PCO correction carries no residential block on its form, and the
          retailer's address is not the applicant's. When neither the PAN form
          nor the login left one, say so — a sworn affidavit must not go out
          with a blank where the applicant lives. */}
      {prefillSource !== "LOADING" && !address.trim() && (
        <div
          style={{
            padding: "8px 20px",
            fontSize: "12.5px",
            borderBottom: "1px solid #ddd",
            background: "#fdecea",
            color: "#8a1c12",
            fontWeight: 500,
          }}
        >
          No residential address found on the PAN form or the application. Use
          Edit to type the applicant's residential address before downloading.
        </div>
      )}

      {/* Editable Fields - shown only when Edit button is clicked */}
      {isEditing && (
        <div
          style={{
            padding: "15px 20px",
            backgroundColor: "#f5f5f5",
            borderBottom: "1px solid #ddd",
            display: "flex",
            flexDirection: "column",
            gap: "12px",
          }}
        >
          <div style={{ fontWeight: 600, fontSize: "14px", marginBottom: "4px" }}>
            Edit Details
          </div>
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: "200px" }}>
              <label style={{ fontSize: "12px", fontWeight: 500, display: "block", marginBottom: "4px" }}>
                Applicant Name
              </label>
              <input
                type="text"
                value={applicantName}
                onChange={(e) => setApplicantName(e.target.value)}
                style={{
                  width: "100%",
                  padding: "8px 10px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  fontSize: "14px",
                }}
              />
            </div>
            <div style={{ flex: 1, minWidth: "200px" }}>
              <label style={{ fontSize: "12px", fontWeight: 500, display: "block", marginBottom: "4px" }}>
                Father's Name
              </label>
              <input
                type="text"
                value={fatherName}
                onChange={(e) => setFatherName(e.target.value)}
                style={{
                  width: "100%",
                  padding: "8px 10px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  fontSize: "14px",
                }}
              />
            </div>
          </div>
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: "200px" }}>
              <label style={{ fontSize: "12px", fontWeight: 500, display: "block", marginBottom: "4px" }}>
                Date of Birth (DD/MM/YYYY)
              </label>
              <input
                type="text"
                value={dob}
                onChange={(e) => setDob(e.target.value)}
                style={{
                  width: "100%",
                  padding: "8px 10px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  fontSize: "14px",
                }}
                placeholder="DD/MM/YYYY"
              />
            </div>
            <div style={{ flex: 1, minWidth: "200px" }}>
              <label style={{ fontSize: "12px", fontWeight: 500, display: "block", marginBottom: "4px" }}>
                Place
              </label>
              <input
                type="text"
                value={place}
                onChange={(e) => setPlace(e.target.value)}
                style={{
                  width: "100%",
                  padding: "8px 10px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  fontSize: "14px",
                }}
              />
            </div>
          </div>
          {signature && (
            <label style={{ fontSize: "13px", display: "flex", alignItems: "center", gap: "6px" }}>
              <input
                type="checkbox"
                checked={printSignature}
                onChange={(e) => setPrintSignature(e.target.checked)}
              />
              Print applicant's signature
            </label>
          )}
          <div>
            <label style={{ fontSize: "12px", fontWeight: 500, display: "block", marginBottom: "4px" }}>
              Address
            </label>
            <input
              type="text"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              style={{
                width: "100%",
                padding: "8px 10px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                fontSize: "14px",
              }}
            />
          </div>
        </div>
      )}

      {/* Serial number bar. Outside #pdfContent, never in the PDF. */}
      {prefillSource !== "LOADING" && (
        <div
          style={{
            padding: "8px 20px",
            fontSize: "12.5px",
            borderBottom: "1px solid #ddd",
            background: "#f3effa",
            color: "#333",
          }}
        >
          {!notaryOn
            ? "Notary is OFF in Affidavit Stamp & Seal: plain affidavit, no tickets, seals or serial number."
            : previewSerial
            ? hasOwnSerial
              ? `Sr. No. ${previewSerial.serialLabel} — this application's number, printed again.`
              : `Sr. No. ${previewSerial.serialLabel} — the next free number; it is given to this application when you press Download.`
            : "No serial start number is set in Affidavit Stamp & Seal, so NOTED & REGISTERED is not printed."}
          {isUpdate &&
            " Updating here does not change the copy inside the PAN form PDF — use Refill PAN Form for that."}
        </div>
      )}

      <div>
        <AffidavitTemplate
          applicantName={applicantName}
          fatherName={fatherName}
          dob={dob}
          address={address}
          place={place}
          todayDate={todayDate}
          signature={printSignature ? signature : ""}
          acknowledgementNumber={businessEnquiryData?.acknowledgementNumber}
          stamps={stamps}
          marks={previewMarks}
        />
      </div>

      <div className="flex bottom-0 right-0 p-3  justify-end gap-3 absolute bg-white z-50 w-full">
        <ATMLoadingButton
          className="border-primary-main text-primary-main bg-white"
          onClick={onClose}
        >
          Close
        </ATMLoadingButton>

        {!isEditing ? (
          <ATMLoadingButton
            className="border-primary-main text-white bg-yellow-500"
            onClick={() => setIsEditing(true)}
          >
            <div className="font-medium flex items-center">
              <span>Edit</span>
            </div>
          </ATMLoadingButton>
        ) : (
          <ATMLoadingButton
            className="border-primary-main text-white bg-green-600"
            onClick={() => setIsEditing(false)}
          >
            <div className="font-medium flex items-center">
              <span>Save</span>
            </div>
          </ATMLoadingButton>
        )}

        <ATMLoadingButton
          type="submit"
          onClick={() => generatePDF()}
          className="bg-primary-main"
          loadingText="Generating..."
          isLoading={isGenerating}
          disabled={isGenerating || prefillSource === "LOADING" || !stamps}
        >
          <div className=" font-medium  flex items-center">
            <span>{isUpdate ? "Update & Download" : "Download"}</span>
          </div>
        </ATMLoadingButton>
      </div>
    </>
  );
};

export default DownloadDialog;
